import { format, parseISO } from 'date-fns';
import { Calendar, EllipsisVertical, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import {
  getCoordinationProcessMode,
  modeBadgeConfig,
  statusBadgeConfig,
} from '@/modules/coordination/components/CoordinationControlPanel';
import { useUpdateCoordinationProcessStatusMutation } from '@/modules/coordination/mutations/useUpdateCoordinationProcessStatusMutation';
import { useCoordinationProcessesQuery } from '@/modules/coordination/queries/useCoordinationProcessesQuery';
import type {
  CoordinationProcessApi,
  CoordinationScheduledMeetingApi,
} from '@/modules/coordination/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface CoordinationProcessesSectionProps {
  jobId: string;
}

interface ProcessDisplayInfo {
  inReviewCount: number;
  meeting: CoordinationScheduledMeetingApi | undefined;
  closedAt: string | undefined;
  isClosed: boolean;
}

function getProcessDisplayInfo(
  process: CoordinationProcessApi,
): ProcessDisplayInfo {
  const inReviewCount = process.aiServiceContext.pendingReviewActions.filter(
    (action) => action.status === 'InReview',
  ).length;

  const meetings = process.aiServiceContext.scheduledMeetings;
  const closedAt = process.statusHistories
    .filter((history) => history.toStatus === 'Finished')
    .at(-1)?.createdAt;
  const isClosed = process.status === 'Finished' || process.status === 'Failed';

  if (isClosed) {
    return {
      inReviewCount,
      meeting: undefined,
      closedAt,
      isClosed,
    };
  }

  const upcoming = [...meetings].sort(
    (a, b) => parseISO(a.startsAt).getTime() - parseISO(b.startsAt).getTime(),
  );

  return {
    inReviewCount,
    meeting: upcoming.at(0),
    closedAt: undefined,
    isClosed,
  };
}

function getHiringManagerName(
  hiringManager: CoordinationProcessApi['hiringManager'],
) {
  if (!hiringManager) return null;
  const fullName =
    `${hiringManager.firstName ?? ''} ${hiringManager.lastName ?? ''}`.trim();
  return fullName || hiringManager.email || null;
}

export function CoordinationProcessesSection({
  jobId,
}: CoordinationProcessesSectionProps) {
  const processesQuery = useCoordinationProcessesQuery(jobId);

  const isPending = processesQuery.isPending;
  const processes = processesQuery.data ?? [];

  if (!isPending && processes.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <Subheading variant="h5">Coordination</Subheading>
      <div className="flex flex-col divide-y rounded-lg border">
        {isPending
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="px-4 py-3">
                <Skeleton className="h-4 w-40" />
              </div>
            ))
          : processes.map((process) => {
              const candidateName = `${process.candidate.firstName} ${process.candidate.lastName}`;
              const hiringManagerName = getHiringManagerName(
                process.hiringManager,
              );
              const { inReviewCount, meeting, closedAt, isClosed } =
                getProcessDisplayInfo(process);

              return (
                <CoordinationProcessRow
                  key={process.id}
                  jobId={jobId}
                  coordinationProcessId={process.id}
                  candidateName={candidateName}
                  hiringManagerName={hiringManagerName}
                  inReviewCount={inReviewCount}
                  meeting={meeting}
                  closedAt={closedAt}
                  isClosed={isClosed}
                  processStatus={process.status}
                  isCoordinationActionAutoApproved={
                    process.isCoordinationActionAutoApproved
                  }
                />
              );
            })}
      </div>
    </section>
  );
}

interface CoordinationProcessRowProps {
  jobId: string;
  coordinationProcessId: string;
  candidateName: string;
  hiringManagerName: string | null;
  inReviewCount: number;
  meeting: CoordinationScheduledMeetingApi | undefined;
  closedAt: string | undefined;
  isClosed: boolean;
  processStatus: CoordinationProcessApi['status'];
  isCoordinationActionAutoApproved: boolean;
}

function CoordinationProcessRow({
  jobId,
  coordinationProcessId,
  candidateName,
  hiringManagerName,
  inReviewCount,
  meeting,
  closedAt,
  isClosed,
  processStatus,
  isCoordinationActionAutoApproved,
}: CoordinationProcessRowProps) {
  const {
    label: processStatusLabel,
    className: processStatusClassName,
    tooltip: processStatusTooltip,
  } = statusBadgeConfig[processStatus];
  const {
    label: modeLabel,
    className: modeClassName,
    tooltip: modeTooltip,
  } = modeBadgeConfig[
    getCoordinationProcessMode(isCoordinationActionAutoApproved)
  ];

  return (
    <div className="hover:bg-muted/40 flex items-center gap-2 px-4 py-3">
      <Link
        to={`/jobs/${jobId}/coordination/${coordinationProcessId}`}
        className="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium">
            {candidateName}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                size="sm"
                className={cn('border-transparent', processStatusClassName)}
              >
                {processStatusLabel}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{processStatusTooltip}</TooltipContent>
          </Tooltip>
          {inReviewCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  size="sm"
                  className="bg-semantic-540/10 text-semantic-540 border-transparent"
                >
                  <TriangleAlert className="size-3" />
                  Needs review
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                One or more AI-planned actions are waiting for approval.
              </TooltipContent>
            </Tooltip>
          )}
          {meeting && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  size="sm"
                  className="bg-secondary-500/10 text-secondary-500 border-transparent"
                >
                  <Calendar className="size-3" />
                  Upcoming meeting
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                A meeting has been scheduled and is coming up.
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                size="sm"
                className={cn('border-transparent', modeClassName)}
              >
                {modeLabel}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{modeTooltip}</TooltipContent>
          </Tooltip>
          {inReviewCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  size="sm"
                  className="bg-semantic-540 border-transparent text-white"
                >
                  {inReviewCount} to review
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {inReviewCount === 1
                  ? '1 action is waiting for manual review.'
                  : `${inReviewCount} actions are waiting for manual review.`}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <Paragraph variant="caption">
            {hiringManagerName
              ? `Hiring manager: ${hiringManagerName}`
              : 'Hiring manager not assigned'}
          </Paragraph>
          <Paragraph variant="caption" className="shrink-0">
            |
          </Paragraph>
          <Paragraph variant="caption">
            {meeting
              ? format(parseISO(meeting.startsAt), "EEE, MMM d 'at' h:mm a")
              : closedAt
                ? `Closed ${format(parseISO(closedAt), "EEE, MMM d 'at' h:mm a")}`
                : isClosed
                  ? 'Closed'
                  : 'No meeting scheduled yet'}
          </Paragraph>
        </div>
      </Link>
      <CoordinationProcessRowActions
        jobId={jobId}
        coordinationProcessId={coordinationProcessId}
        status={processStatus}
        isCoordinationActionAutoApproved={isCoordinationActionAutoApproved}
      />
    </div>
  );
}

interface CoordinationProcessRowActionsProps {
  jobId: string;
  coordinationProcessId: string;
  status: CoordinationProcessApi['status'];
  isCoordinationActionAutoApproved: boolean;
}

function CoordinationProcessRowActions({
  jobId,
  coordinationProcessId,
  status,
  isCoordinationActionAutoApproved,
}: CoordinationProcessRowActionsProps) {
  const updateStatusMutation = useUpdateCoordinationProcessStatusMutation(
    jobId,
    coordinationProcessId,
  );

  if (status !== 'Active' && status !== 'Paused') {
    return null;
  }

  const nextStatus = status === 'Active' ? 'Paused' : 'Active';
  const statusLabel =
    status === 'Active' ? 'Pause coordination' : 'Resume coordination';
  const modeLabel = isCoordinationActionAutoApproved
    ? 'Require manual review'
    : 'Enable auto-approve';

  const handleStatusClick = () => {
    updateStatusMutation.mutate(
      { status: nextStatus, isCoordinationActionAutoApproved },
      {
        onSuccess: () => {
          toast.success(
            nextStatus === 'Active'
              ? 'Coordination resumed'
              : 'Coordination paused',
          );
        },
        onError: (error) => {
          toast.error(
            `Failed to update coordination status: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleModeClick = () => {
    const nextIsCoordinationActionAutoApproved =
      !isCoordinationActionAutoApproved;
    updateStatusMutation.mutate(
      {
        status,
        isCoordinationActionAutoApproved: nextIsCoordinationActionAutoApproved,
      },
      {
        onSuccess: () => {
          toast.success(
            nextIsCoordinationActionAutoApproved
              ? 'Auto-approve enabled'
              : 'Manual review enabled',
          );
        },
        onError: (error) => {
          toast.error(
            `Failed to update coordination mode: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleCloseClick = () => {
    updateStatusMutation.mutate(
      { status: 'Finished', isCoordinationActionAutoApproved },
      {
        onSuccess: () => {
          toast.success('Coordination closed');
        },
        onError: (error) => {
          toast.error(
            `Failed to close coordination: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-primary hover:text-primary shrink-0 cursor-pointer"
        >
          <EllipsisVertical size={20} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={handleStatusClick}
          disabled={updateStatusMutation.isPending}
        >
          {statusLabel}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleModeClick}
          disabled={updateStatusMutation.isPending}
        >
          {modeLabel}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleCloseClick}
          disabled={updateStatusMutation.isPending}
        >
          Close coordination
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
